import { useState, useEffect, useCallback } from "react";
import { AppState, Deck, Folder } from "@/types/linguadeck";
import { useAuth } from "@/contexts/AuthContext";
import { useThemeStore } from "@/stores/useThemeStore";
import {
  fetchDecks,
  fetchDecksLight,
  fetchFolders,
  fetchCardsByDeckId,
  fetchDeckById,
  createDeck,
  createDecksBulk,
  createFolder,
  updateDeckInDB,
  deleteDeckFromDB,
} from "@/lib/db";
import { logFlowDiagnostic, classifySupabaseError } from "@/diagnostics";
import { triggerBackgroundTtsForCards } from "@/lib/triggerBackgroundTts";
import { migrateLocalStorageToSupabase, isMigrationDone } from "@/lib/migration";
import { getStoredGeminiKeys, getStoredPollinationsKeys } from "@/lib/apiKeys";

// localStorage keys (kept for non-deck settings)
const POINTS_KEY = "ld_points";
const CRYSTALS_KEY = "ld_crystals";
const STREAK_KEY = "ld_streak";
const LAST_ACTIVITY_KEY = "ld_last_activity";

const defaultState = (): Omit<AppState, "decks" | "loading"> => ({
  currentDeckId: null,
  currentView: "home",
  cardIndex: 0,
  cardFlipped: false,
  points: parseInt(localStorage.getItem(POINTS_KEY) || "0"),
  crystals: parseInt(localStorage.getItem(CRYSTALS_KEY) || "0"),
  streak: parseInt(localStorage.getItem(STREAK_KEY) || "0"),
  lastActivityDate: localStorage.getItem(LAST_ACTIVITY_KEY),
  uiLang: "ru",
  theme: (useThemeStore.getState().systemTheme as AppState["theme"]) || "system",
  aiModel: localStorage.getItem("ld_model") || "gemini-fast",
  pollinationsKey: getStoredPollinationsKeys(),
  geminiKey: getStoredGeminiKeys(),
});

export function useAppState() {
  const { user } = useAuth();

  const [state, setState] = useState<AppState>(() => ({
    ...defaultState(),
    decks: [],
    folders: [],
    loading: true,
  }));

  // ── Load decks from Supabase when user changes ──────────────────────────────
  useEffect(() => {
    if (!user?.id) {
      setState((prev) => ({
        ...prev,
        decks: [],
        folders: [],
        currentDeckId: null,
        loading: false,
      }));
      return;
    }

    setState((prev) => ({ ...prev, loading: true }));

    const loadDecks = async () => {
      // ── One-time migration from localStorage → Supabase ──────────────────
      if (!isMigrationDone()) {
        try {
          const result = await migrateLocalStorageToSupabase(user.id);
          if (result.migrated > 0) {
            console.log(
              `[migration] Migrated ${result.migrated} deck(s) to Supabase.`
            );
          }
          if (result.errors.length > 0) {
            console.warn("[migration] Some decks failed to migrate:", result.errors);
          }
        } catch (err) {
          console.error("[migration] Unexpected migration error:", err);
        }
      }

      // ── Fast first paint: fetch first 96 decks, clear loading; then full decks + folders in background ───
      const uid = user.id;
      const INITIAL_DECK_LIMIT = 96;
      try {
        const foldersPromise = fetchFolders(uid);
        const initialDecks = await fetchDecksLight(uid, INITIAL_DECK_LIMIT);
        const useInitial = initialDecks as Deck[];
        setState((prev) => {
          const fetchedIds = new Set(useInitial.map((d) => d.id));
          const prevDecks = Array.isArray(prev.decks) ? prev.decks : [];
          const locallyAdded = prevDecks.filter((d) => !fetchedIds.has(d.id));
          const mergedRaw = [...useInitial, ...locallyAdded];
          const merged = mergedRaw.filter((d, i, arr) => arr.findIndex((x) => x.id === d.id) === i);
          return {
            ...prev,
            decks: merged,
            currentDeckId: prev.currentDeckId || merged[0]?.id || null,
            loading: false,
          };
        });
        (async () => {
          try {
            const hasMoreDecks = useInitial.length >= INITIAL_DECK_LIMIT;
            const [fullDecks, folders] = await Promise.all([
              hasMoreDecks ? fetchDecksLight(uid) : Promise.resolve(useInitial),
              foldersPromise,
            ]);
            const useFull = fullDecks as Deck[];
            setState((prev) => {
              const fetchedIds = new Set(useFull.map((d) => d.id));
              const prevDecks = Array.isArray(prev.decks) ? prev.decks : [];
              const locallyAdded = prevDecks.filter((d) => !fetchedIds.has(d.id));
              const mergedRaw = [...useFull, ...locallyAdded];
              let merged = mergedRaw.filter((d, i, arr) => arr.findIndex((x) => x.id === d.id) === i);
              const currentId = prev.currentDeckId;
              if (currentId) {
                const inPrev = prevDecks.find((d) => d.id === currentId);
                const inMerged = merged.find((d) => d.id === currentId);
                if ((inPrev?.cards?.length ?? 0) > 0 && (inMerged?.cards?.length ?? 0) === 0 && inPrev) {
                  merged = merged.map((d) => (d.id === currentId ? inPrev : d));
                }
              }
              return {
                ...prev,
                decks: merged,
                folders: Array.isArray(folders) ? folders : [],
                currentDeckId:
                  prev.currentDeckId && merged.some((d) => d.id === prev.currentDeckId)
                    ? prev.currentDeckId
                    : merged[0]?.id || null,
              };
            });
          } catch (e) {
            console.warn("[useAppState] Background deck/folder merge failed:", e);
          }
        })();
      } catch (err) {
        const classified = classifySupabaseError(err);
        logFlowDiagnostic({
          flow: "library.loadDecks",
          operation: "select",
          entity: "deck",
          userId: uid,
          success: false,
          error: err,
          classified,
        });
        console.error("[useAppState] Failed to load decks:", err);
        setState((prev) => ({
          ...prev,
          decks: [],
          folders: [],
          currentDeckId: null,
          loading: false,
        }));
      }
    };

    loadDecks();
  }, [user]);

  // ── Keep state.theme in sync with useThemeStore (ThemeApplicator applies it) ──
  const systemTheme = useThemeStore((s) => s.systemTheme);
  useEffect(() => {
    setState((prev) => (prev.theme !== systemTheme ? { ...prev, theme: systemTheme } : prev));
  }, [systemTheme]);

  // ── Actions ─────────────────────────────────────────────────────────────────

  const updateState = useCallback(
    (updates: Partial<AppState> | ((prev: AppState) => Partial<AppState>)) => {
      setState((prev) => ({
        ...prev,
        ...(typeof updates === "function" ? updates(prev) : updates),
      }));
    },
    []
  );

  const saveDecks = useCallback(
    (decks: Deck[]) => {
      setState((prev) => ({ ...prev, decks }));
    },
    []
  );

  const refreshDecks = useCallback(async () => {
    if (!user) return;
    try {
      const decks = await fetchDecksLight(user.id) as Deck[];
      const useDecks = decks;
      setState((prev) => {
        const fetchedIds = new Set(useDecks.map((d) => d.id));
        const prevDecks = Array.isArray(prev.decks) ? prev.decks : [];
        const locallyAdded = prevDecks.filter((d) => !fetchedIds.has(d.id));
        const mergedRaw = [...useDecks, ...locallyAdded];
        let merged = mergedRaw.filter((d, i, arr) => arr.findIndex((x) => x.id === d.id) === i);
        const currentId = prev.currentDeckId;
        if (currentId) {
          const inPrev = prevDecks.find((d) => d.id === currentId);
          const inMerged = merged.find((d) => d.id === currentId);
          const prevHasCards = (inPrev?.cards?.length ?? 0) > 0;
          const mergedHasCards = (inMerged?.cards?.length ?? 0) > 0;
          if (prevHasCards && !mergedHasCards && inPrev) {
            merged = merged.map((d) => (d.id === currentId ? inPrev : d));
          }
        }
        return {
          ...prev,
          decks: merged,
          currentDeckId:
            prev.currentDeckId && merged.some((d) => d.id === prev.currentDeckId)
              ? prev.currentDeckId
              : merged[0]?.id || null,
        };
      });
    } catch (err) {
      console.error("Failed to refresh decks:", err);
    }
  }, [user]);

  const addDeck = useCallback(
    async (deck: Deck): Promise<Deck> => {
      if (user) {
        try {
          const created = await createDeck(user.id, deck);
          setState((prev) => {
            const prevDecks = Array.isArray(prev.decks) ? prev.decks : [];
            return {
              ...prev,
              decks: [...prevDecks, created],
              currentDeckId: created.id,
            };
          });
          if (created.cards?.length) {
            triggerBackgroundTtsForCards(created.cards, created.lang ?? "en");
          }
          // Refetch library to ensure server consistency (deck appears after refresh)
          try {
            await refreshDecks();
          } catch {
            // Optimistic state already set; refetch failure is non-fatal
          }
          return created;
        } catch (err: unknown) {
          console.error("[addDeck] createDeck failed:", err);
          const { toast } = await import("sonner");
          const i18n = (await import("@/i18n/i18n")).default;
          toast.error(i18n.t("errors.deckSaveFailed"));
          throw err;
        }
      } else {
        const deckWithDate = {
          ...deck,
          id: deck.id ?? "deck_" + Date.now(),
          created_at: deck.created_at ?? new Date().toISOString(),
        };
        setState((prev) => {
          const prevDecks = Array.isArray(prev.decks) ? prev.decks : [];
          return {
            ...prev,
            decks: [...prevDecks, deckWithDate],
            currentDeckId: deckWithDate.id,
          };
        });
        return deckWithDate;
      }
    },
    [user, refreshDecks]
  );

  const addDecks = useCallback(
    async (decksData: Omit<Deck, "id">[]): Promise<{ created: Deck[]; failed: Array<{ index: number; title: string; error: Error }> }> => {
      if (!user) {
        const deckWithDate = (d: Omit<Deck, "id">, i: number) => ({
          ...d,
          id: `deck_${Date.now()}_${i}`,
          created_at: new Date().toISOString(),
        });
        const created = decksData.map((d, i) => deckWithDate(d, i) as Deck);
        setState((prev) => {
          const prevDecks = Array.isArray(prev.decks) ? prev.decks : [];
          return {
            ...prev,
            decks: [...prevDecks, ...created],
            currentDeckId: created[0]?.id || prev.currentDeckId,
          };
        });
        return { created, failed: [] };
      }

      const { created, failed } = await createDecksBulk(user.id, decksData);

      if (created.length > 0) {
        setState((prev) => {
          const prevDecks = Array.isArray(prev.decks) ? prev.decks : [];
          return {
            ...prev,
            decks: [...prevDecks, ...created],
            currentDeckId: created[0].id,
          };
        });
        for (const deck of created) {
          if (deck.cards?.length) {
            triggerBackgroundTtsForCards(deck.cards, deck.lang ?? "en");
          }
        }
        try {
          await refreshDecks();
        } catch {
          /* non-fatal */
        }
      }

      return { created, failed };
    },
    [user, refreshDecks]
  );

  const deleteDeck = useCallback(
    async (id: string) => {
      if (user) {
        try {
          await deleteDeckFromDB(id);
        } catch (err) {
          console.error("Failed to delete deck:", err);
          return; // Keep deck in state on server rejection
        }
      }
      setState((prev) => {
        const newDecks = prev.decks.filter((d) => d.id !== id);
        return {
          ...prev,
          decks: newDecks,
          currentDeckId: newDecks[0]?.id || null,
          currentView: newDecks.length ? prev.currentView : "home",
        };
      });
    },
    [user]
  );

  const updateDeck = useCallback(
    async (id: string, updates: Partial<Deck>) => {
      if (user) {
        try {
          const result = await updateDeckInDB(id, updates);
          const cardsToMerge = result && "insertedCards" in result ? result.insertedCards : undefined;
          setState((prev) => {
            const deck = prev.decks.find((d) => d.id === id);
            const deckLang = updates.lang ?? deck?.lang ?? "en";
            if (cardsToMerge?.length) {
              triggerBackgroundTtsForCards(cardsToMerge, deckLang);
            }
            return {
              ...prev,
              decks: prev.decks.map((d) => {
                if (d.id !== id) return d;
                const next = { ...d, ...updates };
                if (cardsToMerge?.length) next.cards = cardsToMerge;
                return next;
              }),
            };
          });
        } catch (err) {
          console.error("Failed to update deck:", err);
          // Do not apply updates on server rejection; keep existing state
        }
      } else {
        setState((prev) => ({
          ...prev,
          decks: prev.decks.map((d) => (d.id === id ? { ...d, ...updates } : d)),
        }));
      }
    },
    [user]
  );

  /** Merge updates into a single card (local state only). Use after updateCardInDB for single-card edits. */
  const mergeCardInDeck = useCallback((deckId: string, cardIndex: number, updates: Partial<Deck["cards"][0]>) => {
    setState((prev) => ({
      ...prev,
      decks: prev.decks.map((d) => {
        if (d.id !== deckId || !d.cards?.[cardIndex]) return d;
        const newCards = [...d.cards];
        newCards[cardIndex] = { ...newCards[cardIndex], ...updates };
        return { ...d, cards: newCards };
      }),
    }));
  }, []);

  const addPoints = useCallback((pts: number) => {
    setState((prev) => {
      const newPoints = prev.points + pts;
      localStorage.setItem(POINTS_KEY, String(newPoints));
      return { ...prev, points: newPoints };
    });
  }, []);

  const navTo = useCallback((view: AppState["currentView"]) => {
    setState((prev) => ({
      ...prev,
      currentView: view,
      cardIndex: 0,
      cardFlipped: false,
    }));
  }, []);

  const selectDeck = useCallback((id: string) => {
    setState((prev) => ({
      ...prev,
      currentDeckId: id,
      cardIndex: 0,
      cardFlipped: false,
      currentView: "flashcards",
    }));
  }, []);

  const addFolder = useCallback(
    async (title: string): Promise<Folder | null> => {
      if (!user) return null;
      try {
        const folder = await createFolder(user.id, title);
        setState((prev) => ({
          ...prev,
          folders: [...(prev.folders ?? []), folder],
        }));
        return folder;
      } catch (err) {
        console.error("Failed to create folder:", err);
        return null;
      }
    },
    [user]
  );

  const refreshFolders = useCallback(async () => {
    if (!user) return;
    try {
      const folders = await fetchFolders(user.id);
      setState((prev) => ({ ...prev, folders }));
    } catch (err) {
      console.error("Failed to refresh folders:", err);
    }
  }, [user]);

  /** Refetch a deck from DB and replace it in state. Use after saving a card so the UI reflects the persisted change. */
  const refetchDeck = useCallback(async (deckId: string): Promise<void> => {
    try {
      const full = await fetchDeckById(deckId);
      if (full) {
        setState((prev) => ({
          ...prev,
          decks: prev.decks.map((d) => (d.id === deckId ? full : d)),
        }));
      }
    } catch (err) {
      console.warn("[useAppState] refetchDeck failed:", err);
    }
  }, []);

  /** Load deck with cards for study view. If deck is in state but has no cards, fetches cards; if not in state, fetches full deck. */
  const ensureDeckWithCards = useCallback(async (deckId: string): Promise<Deck | null> => {
    const deck = state.decks.find((d) => d.id === deckId);
    if (deck?.cards?.length > 0) return deck;
    try {
      if (deck) {
        const cards = await fetchCardsByDeckId(deckId);
        setState((prev) => ({
          ...prev,
          decks: prev.decks.map((d) =>
            d.id === deckId ? { ...d, cards } : d
          ),
        }));
        return { ...deck, cards };
      }
      const fullDeck = await fetchDeckById(deckId);
      if (fullDeck) {
        setState((prev) => ({
          ...prev,
          decks: prev.decks.some((d) => d.id === deckId)
            ? prev.decks.map((d) => (d.id === deckId ? fullDeck : d))
            : [...prev.decks, fullDeck],
        }));
      }
      return fullDeck;
    } catch (err) {
      console.error("Failed to load deck cards:", err);
      return null;
    }
  }, [state.decks]);

  return {
    state,
    updateState,
    saveDecks,
    addDeck,
    addDecks,
    deleteDeck,
    updateDeck,
    mergeCardInDeck,
    addFolder,
    refreshDecks,
    refreshFolders,
    ensureDeckWithCards,
    refetchDeck,
    addPoints,
    navTo,
    selectDeck,
  };
}
